/**
 * Vorlage „Eier trennen“.
 *
 * Über der Schüssel wird der Dotter zwischen den beiden Schalenhälften hin
 * und her gegeben, das Eiweiß läuft dabei in die Schüssel. Zum Schluss
 * gleitet der Dotter in ein eigenes Schälchen.
 */
import { ease, lerp, segment } from '../geometry.ts';
import type { Point } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { path } from '../paths.ts';
import { circle, compact, faded, group, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { stream } from '../parts/kitchen.ts';
import { arrow, counter, GROUND_Y } from '../parts/stage.ts';
import { bowl, contents } from '../parts/vessels.ts';
import { defineTemplate } from '../template.ts';

/** Schüssel für das Eiweiß und Schälchen für den Dotter. */
const BOWL = { cx: 82, rim: 90, width: 124, height: 46 } as const;
const CUP = { cx: 198, rim: GROUND_Y - 22, width: 54, height: 22 } as const;
/** Lage der Schalenhälften über der Schüssel. */
const LEFT = { x: 68, y: 50 } as const;
const RIGHT = { x: 98, y: 50 } as const;
const YOLK_R = 6.5;

/** Zeitplan. */
const TIME = {
  appear: [0.02, 0.08],
  hops: [0.1, 0.56],
  white: [0.06, 0.58],
  leftOut: [0.56, 0.62],
  toCup: [0.58, 0.7],
  tip: [0.7, 0.76],
  drop: [0.74, 0.82],
  rightOut: [0.8, 0.86],
  reset: [0.88, 1],
} as const;

/** Schalenhälfte, Öffnung oben, Mitte der Öffnung im Ursprung. */
function shell(): SceneNode[] {
  const outline = path()
    .move(-13, 0)
    .arc(13, 11, false, false, 13, 0)
    .line(10, -2)
    .line(7, 1)
    .line(3, -2)
    .line(-1, 1)
    .line(-5, -2)
    .line(-9, 1)
    .close()
    .toString();
  return [shape(outline, styles.solid('paper'))];
}

/** Dotter. */
function yolk(at: Point): SceneNode {
  return circle(at.x, at.y, YOLK_R, { fill: 'shade', stroke: 'ink', strokeWidth: STROKE.detail });
}

/** Vorlage „Eier trennen“. */
export const prepSeparateEggsTemplate = defineTemplate({
  id: 'prep.separate-eggs',
  group: 'prep',
  title: 'Eier trennen',
  description:
    'Der Dotter wird über einer Schüssel zwischen den Schalenhälften hin und her gegeben, bis das Eiweiß abgelaufen ist. Danach kommt er in ein eigenes Gefäß.',
  durationSeconds: 6,
  keywords: [
    'eier trennen',
    'ei trennen',
    'trennen',
    'eigelb und eiweiß',
    'eigelb und eiweiss',
    'eiweiß vom eigelb',
    'dotter',
  ],
  stillTime: 0.3,
  parameters: [],
  scene(_values, t) {
    const vessel = bowl(BOWL.cx, BOWL.rim, BOWL.width, BOWL.height);
    const cup = bowl(CUP.cx, CUP.rim, CUP.width, CUP.height);
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const appear = ease.outCubic(segment(t, ...TIME.appear));

    // Dotter springt dreimal zwischen den Hälften hin und her.
    const hops = segment(t, ...TIME.hops) * 3;
    const hop = Math.min(2, Math.floor(hops));
    const local = ease.inOutSine(hops - hop);
    const from = hop % 2 === 0 ? LEFT : RIGHT;
    const to = hop % 2 === 0 ? RIGHT : LEFT;
    const tilt = (side: -1 | 1): number => {
      // Die abgebende Hälfte kippt zur anderen hin.
      const giving = (hop % 2 === 0 ? -1 : 1) === side && hops > 0 && hops < 3;
      return giving ? side * -28 * Math.sin(local * Math.PI) : side * -8;
    };

    // Rechte Hälfte fährt mit dem Dotter zum Schälchen und kippt.
    const toCup = ease.inOutCubic(segment(t, ...TIME.toCup));
    const tip = ease.inOutCubic(segment(t, ...TIME.tip));
    const cupAbove = { x: CUP.cx - 6, y: CUP.rim - 30 };
    const rightAt = { x: lerp(RIGHT.x, cupAbove.x, toCup), y: lerp(RIGHT.y, cupAbove.y, toCup) };
    const rightTurn = t >= TIME.toCup[0] ? lerp(-8, -80, tip) : tilt(1);

    let yolkAt: Point | null = null;
    if (t < TIME.hops[0]) {
      yolkAt = { x: LEFT.x, y: LEFT.y - 2 };
    } else if (t < TIME.hops[1]) {
      yolkAt = {
        x: lerp(from.x, to.x, local),
        y: lerp(from.y, to.y, local) - 2 - Math.sin(local * Math.PI) * 16,
      };
    } else if (t < TIME.drop[0]) {
      yolkAt = { x: rightAt.x, y: rightAt.y - 2 };
    } else if (t < TIME.drop[1]) {
      const fall = ease.inQuad(segment(t, ...TIME.drop));
      yolkAt = { x: lerp(rightAt.x - 4, CUP.cx, fall), y: lerp(rightAt.y, CUP.rim + 3, fall) };
    }
    const yolkInCup = t >= TIME.drop[1] ? 1 - reset : 0;

    // Eiweiß läuft als dünner Strahl aus den Hälften in die Schüssel.
    const whitePhase = segment(t, ...TIME.white);
    const flow = Math.sin(whitePhase * Math.PI);
    const filled = ease.outQuad(whitePhase) * (1 - reset);
    const level = lerp(0.82, 0.55, filled);
    const surfaceY = BOWL.rim + vessel.depth * level;
    const dripFrom = { x: (LEFT.x + RIGHT.x) / 2, y: LEFT.y + 8 };

    const left = faded(appear * (1 - segment(t, ...TIME.leftOut)), [
      group(shell(), { transform: { x: LEFT.x, y: LEFT.y, rotate: tilt(-1), ox: 0, oy: 0 } }),
    ]);
    const right = faded(appear * (1 - segment(t, ...TIME.rightOut)), [
      group(shell(), { transform: { x: rightAt.x, y: rightAt.y, rotate: rightTurn, ox: 0, oy: 0 } }),
    ]);

    return compact([
      ...counter(),
      ...vessel.back,
      contents(vessel, level, 'snow'),
      flow > 0.05
        ? stream(
            [dripFrom, { x: dripFrom.x - 2, y: dripFrom.y + 18 }, { x: dripFrom.x, y: surfaceY }],
            5 * flow,
            t,
            'snow',
          )
        : null,
      ...vessel.front,
      ...cup.back,
      contents(
        cup,
        0.45,
        'tint',
        yolkInCup > 0 ? [faded(yolkInCup, [yolk({ x: CUP.cx, y: CUP.rim + 4 })])] : [],
      ),
      ...cup.front,
      left,
      yolkAt !== null ? faded(appear, [yolk(yolkAt)]) : null,
      right,
    ]);
  },
  hints() {
    return [
      arrow([
        { x: LEFT.x, y: LEFT.y - 18 },
        { x: (LEFT.x + RIGHT.x) / 2, y: LEFT.y - 34 },
        { x: RIGHT.x, y: RIGHT.y - 18 },
      ]),
      arrow([
        { x: RIGHT.x + 20, y: RIGHT.y - 6 },
        { x: CUP.cx - 16, y: CUP.rim - 30 },
      ]),
    ];
  },
});
